import { useState } from "react";
import "./Pagination.css";

// ─── Icons ───────────────────────────────────────────────────────────────────

const ChevronLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const ChevronDown = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);


// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Pagination
 *
 * @param {number}   currentPage          1-based current page
 * @param {number}   totalItems           total number of items
 * @param {function} onPageChange         (page: number) => void
 * @param {number}   rowsPerPage          currently selected rows per page
 * @param {function} onRowsPerPageChange  (rows: number) => void
 * @param {number[]} rowsPerPageOptions   selectable options, default [10, 20, 50, 100]
 * @param {object}   wrapperStyle         style object for the wrapper div
 */
const Pagination = ({
    currentPage,
    totalItems = 0,
    onPageChange,
    rowsPerPage = 10,
    onRowsPerPageChange,
    wrapperStyle,
    rowsPerPageOptions = [10, 50, 100],
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    
    const safeCurrentPage  = Number.isFinite(currentPage) && currentPage >= 1 ? currentPage : 1;
    const safeTotal = Number.isFinite(totalItems) && totalItems > 0 ? totalItems : 0;
    const safeRows  = Number.isFinite(rowsPerPage) && rowsPerPage > 0 ? rowsPerPage : 10;

    const totalPages = Math.ceil(safeTotal / safeRows);

    // ─── Helpers ─────────────────────────────────────────────────────────────────

    const getPages = (totalPages) => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages = [];
        const current = safeCurrentPage;

        // Always show first
        pages.push(1);

        if (current <= 4) {
            // Near start: 1 2 3 4 5 ... N
            pages.push(2, 3, 4, 5, "...", totalPages);
        } else if (current >= totalPages - 3) {
            // Near end: 1 ... N-4 N-3 N-2 N-1 N
            pages.push("...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        } else {
            // Middle: 1 ... c-1 c c+1 ... N
            pages.push("...", current - 1, current, current + 1, "...", totalPages);
        }

        return pages;
    };

    const handlePageChange = (page) => {
        if (page !== safeCurrentPage && page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    const pages = getPages(totalPages);

    return (
        <div className="pagination-wrapper" style={wrapperStyle}>
            <div className="pagination-container">
                <div className="pg-wrap">
                    {/* ← Prev */}
                    <button
                        className="pg-btn"
                        disabled={safeCurrentPage === 1}
                        onClick={() => handlePageChange(safeCurrentPage - 1)}
                        aria-label="Previous page"
                    >
                        <ChevronLeft />
                    </button>

                    {/* Pages */}
                    {pages.map((page, idx) =>
                        page === "..." ? (
                            <span key={`ellipsis-${idx}`} className="pg-ellipsis" style={{userSelect:"none"}}>···</span>
                        ) : (
                            <button
                                key={page}
                                className={`pg-btn${page === safeCurrentPage ? " pg-btn--active" : ""}`}
                                onClick={() => handlePageChange(page)}
                                aria-label={`Page ${page}`}
                                aria-current={page === safeCurrentPage ? "page" : undefined}
                            >
                                {page}
                            </button>
                        )
                    )}

                    {/* → Next */}
                    <button
                        className="pg-btn"
                        disabled={safeCurrentPage === totalPages || totalPages === 0}
                        onClick={() => handlePageChange(safeCurrentPage + 1)}
                        aria-label="Next page"
                    >
                        <ChevronRight />
                    </button>
                    
                </div>

                {/* Rows per page */}
                <div className="pg-rpp">
                    <span className="pg-rpp-label">По</span>
                    <div className="pg-rpp-dropdown">
                        <button
                            className="pg-rpp-trigger"
                            onClick={() => setDropdownOpen((o) => !o)}
                            onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                        >
                            {safeRows}
                            <ChevronDown />
                        </button>
                        {dropdownOpen && (
                            <div className="pg-rpp-menu">
                                {[10, 50, 100].map((opt) => (
                                    <div
                                        key={opt}
                                        className={`pg-rpp-option${opt === safeRows ? " pg-rpp-option--active" : ""}`}
                                        onClick={() => { onRowsPerPageChange(opt); setDropdownOpen(false); }}
                                    >
                                        {opt}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <span className="pg-rpp-label">на странице</span>
                </div>

            </div>
        </div>
    );
};

export default Pagination;